// src/sections/CheckoutReturn.jsx — landing page after Stripe Checkout
// (route /billing/return?plan=<plan_id>). Always mounted (the build-time billing flag was
// deleted 2026-09-24).
//
// The "payment succeeded" redirect does NOT prove the credits exist: Stripe
// waits for our webhook up to ~10 s before redirecting, so usually they are
// already there — but not always. So this page shows "attivazione in corso…"
// and polls GET /credits/balance (the one endpoint that IS live) until
// `plan_id` equals the expected plan (from ?plan=) or, with no expectation,
// until it differs from the first reading. It never assumes success from the
// URL alone, and it stops after a bounded number of polls with honest copy:
// the payment went through, activation is still propagating, check back.
//
// ⚠️ A checkout never requires re-login: entitlements (plan, credits, formats,
// seats) are read from the DB on every call. Do not add a re-auth prompt here.
//
// v203: nothing claims the payment was received before the plan is seen — the
// polling copy says only that activation is under way. The plan is named by
// `/credits/balance.plan_name` («Piano attivo» without one), never by the raw
// `plan_id`. `?from=console` (set by the Gestione Workspace Piani tab) points
// both links back into the console; it selects one of two allow-listed pairs
// of paths — never a URL taken from the query.
import React, { useEffect, useRef, useState } from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../auth/AuthContext.jsx';
import { fetchCreditBalance } from '../services/api/creditsApi.js';
import { COLORS, FONT_SANS, RADII, SEMANTIC } from '../constants/theme.js';

export const POLL_INTERVAL_MS = 3000;
export const MAX_POLLS = 20; // ~60 s

// The SA-in-adminMode bounce lives in this thin wrapper, NOT inside the view.
// ⚠️ CheckoutReturnView polls /credits/balance from an effect, and an early
// return placed after that hook still lets the first read fire. It is reachable:
// useAuthState rebuilds `activeTenant` from auth_tenant_id and `adminMode` from
// auth_admin_mode INDEPENDENTLY at boot, so "SA in admin mode" and "has an
// active tenant" are not mutually exclusive. The only way not to call a hook is
// not to render the component that holds it.
export default function CheckoutReturn() {
  const { isSuperadmin, adminMode } = useAuth();
  if (isSuperadmin && adminMode) return <Navigate to="/admin/users" replace />;
  return <CheckoutReturnView />;
}

function CheckoutReturnView() {
  const { t } = useTranslation('billing');
  const { activeTenant } = useAuth();
  const { search } = useLocation();
  const query = new URLSearchParams(search);
  const expectedPlan = query.get('plan') || null;
  const fromConsole = query.get('from') === 'console';
  const creditsTo = fromConsole ? '/tenant-admin/manage/credits' : '/credits';
  const plansTo = fromConsole ? '/tenant-admin/manage/plans' : '/billing';
  const tenantId = activeTenant?.tenant_id;

  // 'polling' | 'activated' | 'timeout' | 'noWorkspace'
  const [phase, setPhase] = useState(tenantId ? 'polling' : 'noWorkspace');
  const [planName, setPlanName] = useState(null);
  const firstPlanRef = useRef(undefined);

  useEffect(() => {
    // No tenant → nothing to poll. Say so and hand the user a way out; never
    // leave "activating…" on screen with no poll behind it.
    if (!tenantId) { setPhase('noWorkspace'); return undefined; }
    setPhase('polling');
    let cancelled = false;
    let timer = null;
    let polls = 0;

    async function tick() {
      polls += 1;
      let balance;
      try {
        // `fresh`: /balance is cached 60 s by the browser, and this loop asks
        // for the SAME url up to 20 times. Without it every poll can be served
        // one stored body and the activation stays invisible for the whole
        // budget — the exact false negative this page exists to prevent.
        balance = await fetchCreditBalance({ tenant: tenantId, fresh: true });
      } catch {
        // A transient failure is not a verdict — keep polling within the budget.
        balance = null;
      }
      if (cancelled) return;
      if (balance) {
        if (firstPlanRef.current === undefined) firstPlanRef.current = balance.plan_id ?? null;
        setPlanName(balance.plan_name || null);
        const done = expectedPlan
          ? balance.plan_id === expectedPlan
          : (balance.plan_id ?? null) !== firstPlanRef.current;
        if (done) { setPhase('activated'); return; }
      }
      if (polls >= MAX_POLLS) { setPhase('timeout'); return; }
      timer = setTimeout(tick, POLL_INTERVAL_MS);
    }

    tick();
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, [tenantId, expectedPlan]);

  const box = (colours) => ({
    padding: '16px 18px', borderRadius: RADII.control, fontSize: 14,
    background: colours.bg, color: colours.fg, border: `1px solid ${colours.fg}33`,
  });

  const links = (colour) => (
    <div style={{ marginTop: 10, display: 'flex', gap: 16, flexWrap: 'wrap' }}>
      <Link to={creditsTo} style={{ color: colour, fontWeight: 600 }}>{t('checkout.goToCredits')}</Link>
      <Link to={plansTo} style={{ color: colour, fontWeight: 600 }}>{t('checkout.backToPlans')}</Link>
    </div>
  );

  return (
    <div style={{ padding: '32px 40px', fontFamily: FONT_SANS, color: COLORS.ink, maxWidth: 640 }}>
      <h1 style={{ fontSize: 22, fontWeight: 700, marginBottom: 16 }}>{t('checkout.title')}</h1>

      {phase === 'polling' && (
        <div role="status" aria-live="polite" style={box({ bg: COLORS.bg, fg: COLORS.textSecondary })}>
          <strong style={{ display: 'block', color: COLORS.ink, marginBottom: 4 }}>{t('checkout.activating')}</strong>
          {t('checkout.activatingBody')}
          {links(COLORS.textSecondary)}
        </div>
      )}
      {phase === 'activated' && (
        <div role="status" style={box(SEMANTIC.verde)}>
          <strong style={{ display: 'block', marginBottom: 4 }}>{planName ? t('checkout.activated', { plan: planName }) : t('checkout.activatedNoName')}</strong>
          {t('checkout.activatedBody')}
          {links(SEMANTIC.verde.fg)}
        </div>
      )}
      {phase === 'timeout' && (
        <div role="status" style={box(SEMANTIC.attenzione)}>
          <strong style={{ display: 'block', marginBottom: 4 }}>{t('checkout.stillActivating')}</strong>
          {t('checkout.stillActivatingBody')}
          {links(SEMANTIC.attenzione.fg)}
        </div>
      )}
      {phase === 'noWorkspace' && (
        <div role="status" style={box(SEMANTIC.attenzione)}>
          <strong style={{ display: 'block', marginBottom: 4 }}>{t('checkout.noWorkspace')}</strong>
          {t('checkout.noWorkspaceBody')}
          <div style={{ marginTop: 10 }}><Link to="/select-tenant" style={{ color: SEMANTIC.attenzione.fg, fontWeight: 600 }}>{t('checkout.pickWorkspace')}</Link></div>
        </div>
      )}
    </div>
  );
}
